import { useState, useMemo } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import { useUnistyles } from 'react-native-unistyles';
import IconButton from '@/components/General/IconButton';
import BranchChip from './BranchChip';
import { DEFAULT_EDGE_COLOR } from '../constants';

export default function PathEdge({ id, sourceX, sourceY, targetX, targetY,
    sourcePosition, targetPosition, markerEnd, style, data, selected }) {
    const { theme } = useUnistyles();
    const [hovered, setHovered] = useState(false);
    const [path, labelX, labelY] = getSmoothStepPath({
        sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 12,
    });

    const alwaysShow = useMemo(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia?.('(hover: none)').matches;
    }, []);

    const showControls = !data?.readOnly && (hovered || selected || alwaysShow);

    const edgeStyle = {
        ...style,
        stroke: selected ? theme.colors.primary : (style?.stroke || DEFAULT_EDGE_COLOR),
        strokeWidth: selected ? 3 : (style?.strokeWidth || 2),
        strokeOpacity: data?.dim ? 0.45 : 1,
    };

    return (
        <>
            <BaseEdge id={id} path={path} markerEnd={markerEnd} style={edgeStyle} interactionWidth={24} />
            <EdgeLabelRenderer>
                <div
                    className="nodrag nopan"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={{
                        position: 'absolute',
                        pointerEvents: 'all',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                        zIndex: 1,
                    }}
                >
                    {data.isBranch && <BranchChip label={data.branchLabel} isDefault={data.isDefaultBranch} />}
                    {showControls && (
                        <>
                            <div style={{
                                width: 28,
                                height: 28,
                                borderRadius: 8,
                                backgroundColor: theme.colors.primaryContainer,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <IconButton
                                    iconName="add"
                                    iconSize={14}
                                    buttonSize={28}
                                    type="primary"
                                    testID={`path-edge-insert-${data.pathEdgeID}`}
                                    onPress={() => data.onInsertStep?.({
                                        edgeID: data.pathEdgeID,
                                        sourceStepID: data.sourceStepID,
                                        targetStepID: data.targetStepID,
                                        branchID: data.branchID,
                                        branchLabel: data.branchLabel,
                                        canvasPosition: { x: labelX, y: labelY },
                                    })}
                                />
                            </div>
                            <div style={{
                                width: 28,
                                height: 28,
                                borderRadius: 8,
                                backgroundColor: theme.colors.errorContainer,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <IconButton
                                    iconName="close"
                                    iconSize={14}
                                    buttonSize={28}
                                    type="error"
                                    testID={`path-edge-delete-${data.pathEdgeID}`}
                                    onPress={() => data.onDeleteEdge?.({ edgeID: data.pathEdgeID })}
                                />
                            </div>
                        </>
                    )}
                </div>
            </EdgeLabelRenderer>
        </>
    );
}
